import React from "react";
import { useInView } from "react-intersection-observer";
import {
  Building2,
  Target,
  Award,
  Eye,
  Heart,
  Sparkles,
  Mountain,
} from "lucide-react";

const CompanyAbout: React.FC = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const companyInfo = [
    { label: "社名", value: "株式会社Senrigan" },
    { label: "設立", value: "2000年8月" },
    { label: "資本金", value: "1000万円" },
    { label: "所在地", value: "広島県広島市安佐南区山本2-3-35" },
    { label: "代表者", value: "宮川 清実" },
    {
      label: "事業内容",
      value: "Webシステム受託開発、LP・Webサイト制作、業務アプリ開発、システム保守",
    },
  ];

  const philosophy = {
    mission: {
      icon: Mountain,
      title: "ミッション",
      subtitle: "千の想いを、ひとつのカタチに。",
      description:
        "私たちは千利休の美学を受け継ぎ、無数のアイデアと想いを、シンプルで美しく、本質的価値を持つデジタルソリューションとして形にします。複雑さの中から真の価値を見極め、使う人の心に響く体験を創造することが私たちの使命です。",
      gradient: "from-[#1B2A4A] to-[#4A6FA5]",
    },
    vision: {
      icon: Eye,
      title: "ビジョン",
      subtitle: "デジタル茶室で世界をつなぐ",
      description:
        "2030年、Senriganは「デジタル時代の千利休」として、世界中の企業や個人のアイデアを美しいデジタル体験に変える存在になります。一期一会の精神で、それぞれのクライアントとの出会いを大切にし、その想いを永続的な価値として世界に届けます。",
      gradient: "from-[#4A6FA5] to-[#2D8659]",
    },
  };

  const values = [
    {
      icon: Sparkles,
      title: "侘寂",
      subtitle: "Elegant Simplicity",
      description:
        "無駄を削ぎ落とし、本質だけを残す。複雑な要求をシンプルで美しいソリューションに昇華させる。",
      color: "navy",
    },
    {
      icon: Heart,
      title: "一期一会",
      subtitle: "Treasured Encounters",
      description:
        "すべてのプロジェクト、すべてのクライアントとの出会いを一度きりの貴重な機会として大切にし、全力で向き合う。",
      color: "gold",
    },
    {
      icon: Eye,
      title: "千里眼",
      subtitle: "Visionary Insight",
      description:
        "表面的な課題の奥にある真のニーズを見抜き、未来を見据えた価値あるソリューションを提供する。",
      color: "green",
    },
    {
      icon: Target,
      title: "創意工夫",
      subtitle: "Creative Innovation",
      description:
        "既成概念にとらわれず、常に新しい視点でアイデアをカタチにし、クライアントに驚きと感動を届ける。",
      color: "slate",
    },
    {
      icon: Award,
      title: "心を込めて",
      subtitle: "Heartfelt Craftsmanship",
      description:
        "職人のように一つひとつの作品に魂を込め、使う人の心に響くデジタル体験を丁寧に作り上げる。",
      color: "brown",
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      navy: "bg-[#1B2A4A]/10 text-[#1B2A4A]",
      gold: "bg-[#B8860B]/10 text-[#B8860B]",
      green: "bg-[#2D8659]/10 text-[#2D8659]",
      slate: "bg-[#4A6FA5]/10 text-[#4A6FA5]",
      brown: "bg-[#8B7355]/10 text-[#8B7355]",
    };
    return colors[color] || colors.navy;
  };

  return (
    <div className="min-h-screen py-32 bg-[#F5F3EF]">
      <div className="mx-auto max-w-6xl px-5 lg:px-6">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* ヘッダー */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold text-[#B8860B]">会社概要</span>
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">
              企業理念と<br />
              <span className="text-gradient">会社概要</span>
            </h1>
          </div>

          {/* ミッション・ビジョン */}
          <div className="grid lg:grid-cols-2 gap-6 mb-14">
            {Object.entries(philosophy).map(([key, item]) => {
              const Icon = item.icon;
              return (
                <div
                  key={key}
                  className="relative rounded-2xl border border-[#E8E6E1] bg-white p-8 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-[0.03]`} />
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} shadow-lg`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#2D3748]">{item.title}</h3>
                        <p className={`text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r ${item.gradient}`}>
                          {item.subtitle}
                        </p>
                      </div>
                    </div>
                    <p className="text-[#5A6577] leading-relaxed text-justify">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 価値観 */}
          <div className="mb-14">
            <h2 className="text-2xl font-bold text-center text-[#2D3748] mb-3">
              私たちの価値観
            </h2>
            <p className="text-center text-[#5A6577] mb-10 max-w-2xl mx-auto">
              千利休の美学とデジタル技術を融合させ、5つの価値観を軸に活動しています
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div
                    key={index}
                    className="rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 group"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${getColorClasses(value.color)} transition-all duration-300 group-hover:scale-110`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#2D3748]">{value.title}</h4>
                        <p className="text-xs text-[#7B8794] italic">{value.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-[#5A6577] leading-relaxed text-justify">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 会社概要テーブル */}
          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-8 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1B2A4A]/10">
                <Building2 className="h-6 w-6 text-[#1B2A4A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2D3748]">会社概要</h2>
            </div>

            <dl className="grid md:grid-cols-2 gap-x-8">
              {companyInfo.map((info, index) => (
                <div
                  key={index}
                  className="border-b border-[#E8E6E1] py-4 last:border-b-0"
                >
                  <dt className="text-xs font-bold tracking-widest text-[#7B8794] mb-1">
                    {info.label}
                  </dt>
                  <dd className="text-[#2D3748] font-semibold">
                    {info.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyAbout;
